import { CMS_DARK_PALETTE } from './cms-theme';
import { ThemeContext, ThemeMode, ThemeTokens } from './theme.types';

/** Bind existing Tailwind variable names; private variables only support border/outline roles. */
export function themeVariables(
  tokens: ThemeTokens,
  context: ThemeContext,
  mode: ThemeMode
): Record<string, string> {
  const variables: Record<string, string> = {
    '--color-link': tokens.link,
    '--color-footer': 'var(--aw-cms-color-footer)',
    '--color-footer-contrast': 'var(--aw-cms-color-footer-contrast)',
    '--radius-input': tokens['control-radius'],
    '--color-primary-main': tokens['action-background'],
    '--color-primary-hover': tokens['action-hover-background'],
    '--color-primary-dark': 'var(--aw-cms-color-primary-dark)',
    '--color-primary-on': tokens['action-text'],
    '--color-primary-off': 'var(--aw-cms-color-primary-off)',
    '--color-secondary-main': tokens['secondary-background'],
    '--color-secondary-hover': tokens['secondary-hover-background'],
    '--color-secondary-dark': tokens['secondary-border'],
    '--color-secondary-on': tokens['secondary-text'],
    '--color-success-main': tokens['success'],
    '--color-success-light': 'var(--aw-cms-color-success-light)',
    '--color-success-dark': 'var(--aw-cms-color-success-dark)',
    '--color-success-on': 'var(--aw-cms-color-success-on)',
    '--color-accessibility-fill': 'var(--aw-cms-color-accessibility-fill)',
    '--color-accessibility-light': 'var(--aw-cms-color-accessibility-light)',
    '--color-accessibility-contrast-text':
      'var(--aw-cms-color-accessibility-contrast-text)',
    '--color-input-background': tokens['control-background'],
    '--color-input-ring': tokens['control-border'],
    '--color-input-label': tokens['control-placeholder'],
    '--color-input-focus': tokens['focus-ring'],
    '--color-input-error': tokens['error'],
    '--color-input-accessibility': 'var(--aw-cms-color-input-accessibility)',
    '--color-input-accessibility-text':
      'var(--aw-cms-color-input-accessibility-text)',
    '--color-standard-text-primary': tokens['text-primary'],
    '--color-standard-text-secondary': tokens['text-secondary'],
    '--color-standard-text-disabled': tokens['disabled-text'],
    '--color-standard-text-dark': 'var(--aw-cms-color-standard-text-dark)',
    '--color-standard-outline-dark': tokens['text-primary'],
    '--color-standard-outline-medium': tokens['border'],
    '--color-standard-outline-light': tokens['border'],
    '--color-standard-outline-tonal': tokens['border'],
    '--color-standard-hover-main': tokens['hover'],
    '--color-standard-hover-dark': 'var(--aw-cms-color-standard-hover-dark)',
    '--color-standard-disabled': tokens['disabled-background'],
    '--color-standard-select': tokens['selected'],
    '--color-standard-selected': tokens['selected'],
    '--color-standard-background': tokens['background'],
    '--color-standard-divider': tokens['divider'],
    '--color-standard-surface': tokens['surface'],
    '--color-standard-icon-primary': tokens['link'],
    '--color-standard-icon-disabled': tokens['disabled-text'],
    '--color-warning-main': 'var(--aw-cms-color-warning-main)',
    '--color-warning-light': 'var(--aw-cms-color-warning-light)',
    '--color-warning-dark': 'var(--aw-cms-color-warning-dark)',
    '--color-warning-on': 'var(--aw-cms-color-warning-on)',
    '--color-error-main': tokens['error'],
    '--color-error-hover': tokens['error-hover'],
    '--color-error-light': tokens['error-background'],
    '--color-error-dark': 'var(--aw-cms-color-error-dark)',
    '--color-error-on': tokens['on-error'],
    '--color-chips-license-background':
      'var(--aw-cms-color-chips-license-background)',
    '--color-chips-license-filling':
      'var(--aw-cms-color-chips-license-filling)',
    '--color-info-main': 'var(--aw-cms-color-info-main)',
    '--color-info-light': 'var(--aw-cms-color-info-light)',
    '--color-info-dark': 'var(--aw-cms-color-info-dark)',
    '--color-info-on': 'var(--aw-cms-color-info-on)',
    '--color-info2-main': 'var(--aw-cms-color-info2-main)',
    '--color-info2-light': 'var(--aw-cms-color-info2-light)',
    '--color-info2-dark': 'var(--aw-cms-color-info2-dark)',
    '--color-info2-on': 'var(--aw-cms-color-info2-on)',
    '--color-base-tonal': 'var(--aw-cms-color-base-tonal)',
    '--color-tonal-outlined-border':
      'var(--aw-cms-color-tonal-outlined-border)',
    '--color-outlined-hover': 'var(--aw-cms-color-outlined-hover)',
    '--color-danger': 'var(--aw-cms-color-danger)',
    '--color-danger-hover': 'var(--aw-cms-color-danger-hover)',
    '--color-background-disabled': tokens['disabled-border'],
    '--radius-md': tokens['button-radius'],
    '--color-standard-text': tokens['control-text'],
    '--color-input-text': tokens['control-text'],
    '--aw-primary-border': tokens['action-border'],
    '--aw-primary-outline': tokens['action-outline'],
    '--aw-secondary-border': tokens['secondary-border'],
    '--aw-secondary-outline': tokens['secondary-outline'],
  };
  // Preserve every existing CMS light value exactly, including fixed status colors.
  if (context === 'cms' && mode === 'light') {
    for (const name of Object.keys(variables)) {
      if (
        (name.startsWith('--color-') || name.startsWith('--radius-')) &&
        name !== '--color-standard-text' &&
        name !== '--color-input-text'
      ) {
        variables[name] = `var(--aw-cms-${name.slice(2)})`;
      }
    }
  }
  if (context === 'cms' && mode === 'dark') {
    variables['--color-footer'] = CMS_DARK_PALETTE.footerBackground;
    variables['--color-footer-contrast'] = CMS_DARK_PALETTE.footerContrast;
  }
  return variables;
}
